import { api } from "@/lib/axios";

export interface Property {
  _id: string;
  title: string;
  description: string;
  price: number;
  location: string;
  type: "house" | "apartment" | "land" | "commercial";
  operation: "sale" | "rent";
  features: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    parking: number;
  };
  amenities: string[];
  images: string[];
  status: "active" | "inactive" | "sold" | "rented";
  sellerId: string;
  createdAt: string;
}

export interface CreatePropertyData {
  title: string;
  description: string;
  price: number;
  location: string;
  type: Property["type"];
  operation: Property["operation"];
  features: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    parking: number;
  };
  amenities: string[];
  images: string[];
}

export const getProperties = async (): Promise<Property[]> => {
  const response = await api.get("/api/properties");
  return response.data.properties || [];
};

export const getProperty = async (id: string): Promise<Property> => {
  const response = await api.get(`/api/properties/${id}`);
  return response.data;
};

export const getUserProperties = async (): Promise<Property[]> => {
  try {
    console.log("[properties.ts] Obteniendo propiedades del usuario...");
    const response = await api.get("/api/properties/user/properties");
    console.log("[properties.ts] Propiedades obtenidas:", response.data);
    return response.data;
  } catch (error) {
    console.error("[properties.ts] Error al obtener propiedades del usuario:", error);
    throw error;
  }
};

export const createProperty = async (data: CreatePropertyData): Promise<Property> => {
  const response = await api.post("/api/properties", data);
  return response.data;
};

export const createPropertiesBulk = async (properties: any[]): Promise<{ message: string; insertedCount: number; insertedIds: any }> => {
  const response = await api.post("/api/properties/bulk", { properties });
  return response.data;
};

export const deleteAllProperties = async (): Promise<{ message: string; deletedCount: number }> => {
  const response = await api.delete("/api/properties/bulk-delete-all");
  return response.data;
};

export const getImportPackages = async (): Promise<{ packages: any[] }> => {
  const response = await api.get("/api/properties/import-packages");
  return response.data;
};

export const importPropertyPackage = async (packagePath: string): Promise<{ message: string; insertedCount: number }> => {
  const response = await api.post("/api/properties/bulk-import-package", { packagePath });
  return response.data;
};

export const updateProperty = async (id: string, data: Partial<CreatePropertyData>): Promise<Property> => {
  const response = await api.patch(`/api/properties/${id}`, data);
  return response.data;
};

export const deleteProperty = async (id: string): Promise<void> => {
  await api.delete(`/api/properties/${id}`);
};

export const searchProperties = async (query: string): Promise<Property[]> => {
  const response = await api.get(`/api/properties/search?q=${query}`);
  return response.data;
};

export const filterProperties = async (filters: {
  type?: Property["type"];
  operation?: Property["operation"];
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  minArea?: number;
  maxArea?: number;
}): Promise<Property[]> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(key, value.toString());
    }
  });
  const response = await api.get(`/api/properties/filter?${params.toString()}`);
  return response.data;
};

export const getUserRentedProperties = async () => {
  const response = await api.get("/api/properties/rented");
  return response.data;
};

export const getUserPurchasedProperties = async () => {
  const response = await api.get("/api/properties/purchased");
  return response.data;
};

export const rentProperty = async (id: string) => {
  const response = await api.post(`/api/properties/${id}/rent`);
  return response.data;
};

export const purchaseProperty = async (id: string) => {
  const response = await api.post(`/api/properties/${id}/purchase`);
  return response.data;
};

export const getListedProperties = async (): Promise<Property[]> => {
  try {
    console.log("[properties.ts] Obteniendo propiedades listadas...");
    const response = await api.get("/api/properties/user/properties");
    console.log("[properties.ts] Propiedades listadas obtenidas:", response.data);
    return response.data;
  } catch (error) {
    console.error("[properties.ts] Error al obtener propiedades listadas:", error);
    throw error;
  }
};

export const updatePropertyStatus = async (id: string, status: Property["status"]): Promise<Property> => {
  const response = await api.patch(`/api/properties/${id}/status`, { status });
  return response.data;
}; 